'use client';

import React, { useId } from 'react';
import { Button } from '../../atoms/Button';
import { Select } from '../../atoms/Select';
import { Text } from '../../atoms/Text';
import './Pagination.css';

export interface PaginationProps {
  /**
   * Current page number (1-indexed)
   */
  currentPage: number;
  /**
   * Number of items per page
   */
  pageSize: number;
  /**
   * Total number of items across all pages
   */
  totalItems: number;
  /**
   * Available page size options
   * @default [10, 25, 50, 100]
   */
  pageSizeOptions?: number[];
  /**
   * Callback when page changes
   */
  onPageChange: (page: number) => void;
  /**
   * Callback when page size changes
   */
  onPageSizeChange?: (pageSize: number) => void;
  /**
   * Hide the page size selector
   */
  hidePageSizeSelector?: boolean;
  /**
   * Custom CSS classes
   */
  className?: string;
  /**
   * Inline styles
   */
  style?: React.CSSProperties;
  /**
   * ARIA label for the pagination nav
   */
  'aria-label'?: string;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  pageSize,
  totalItems,
  pageSizeOptions = [10, 25, 50, 100],
  onPageChange,
  onPageSizeChange,
  hidePageSizeSelector = false,
  className = '',
  style,
  'aria-label': ariaLabel = 'Pagination',
}) => {
  const uniqueId = useId();
  const pageSizeSelectId = `pagination-page-size-${uniqueId}`;
  const minPageSize = Math.min(...pageSizeOptions);
  if (totalItems <= minPageSize) return null;
  const totalPages = Math.ceil(totalItems / pageSize);
  const showControls = totalPages > 1;
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  const handlePrevious = () => {
    onPageChange(currentPage - 1);
  };

  const handleNext = () => {
    onPageChange(currentPage + 1);
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newPageSize = parseInt(event.target.value, 10);
    onPageSizeChange?.(newPageSize);
  };

  const handlePageClick = (page: number) => {
    if (page !== currentPage) {
      onPageChange(page);
    }
  };

  // Generate page numbers using sliding window approach
  // Always shows a fixed number of pages to prevent layout shift
  const getPageNumbers = (): number[] => {
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if we have fewer than max
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    // Sliding window: center current page, but clamp to valid range
    let startPage = Math.max(
      1,
      Math.min(currentPage - Math.floor(maxVisiblePages / 2), totalPages - maxVisiblePages + 1)
    );
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    // Ensure we always show exactly maxVisiblePages
    startPage = Math.max(1, endPage - maxVisiblePages + 1);

    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  };

  const pageNumbers = getPageNumbers();

  return (
    <nav
      className={`hi-ui hi-ui-pagination ${className}`.trim()}
      style={style}
      aria-label={ariaLabel}
      role="navigation"
    >
      <div className="hi-ui-pagination-info">
        <Text variant="caption" color="muted" inline>
          Showing {startItem}-{endItem} of {totalItems} items
        </Text>
      </div>

      {showControls && (
        <div className="hi-ui-pagination-controls">
          <Button
            variant="tertiary"
            size="small"
            onClick={handlePrevious}
            disabled={!canGoPrevious}
            aria-label="Go to previous page"
          >
            Previous
          </Button>

          <div className="hi-ui-pagination-pages" role="group" aria-label="Page navigation">
            {pageNumbers.map((page) => (
              <button
                key={page}
                type="button"
                className={`hi-ui-pagination-page-btn ${page === currentPage ? 'hi-ui-pagination-page-btn-current' : ''}`}
                onClick={() => handlePageClick(page)}
                aria-label={`Page ${page}`}
                aria-current={page === currentPage ? 'page' : undefined}
              >
                {page}
              </button>
            ))}
          </div>

          <Button
            variant="tertiary"
            size="small"
            onClick={handleNext}
            disabled={!canGoNext}
            aria-label="Go to next page"
          >
            Next
          </Button>
        </div>
      )}

      {!hidePageSizeSelector && onPageSizeChange && (
        <div className="hi-ui-pagination-page-size">
          <Text variant="caption" color="muted" inline as="label" htmlFor={pageSizeSelectId}>
            Items per page:
          </Text>
          <Select
            id={pageSizeSelectId}
            size="sm"
            value={pageSize}
            onChange={handlePageSizeChange}
            aria-label="Items per page"
          >
            {pageSizeOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
        </div>
      )}
    </nav>
  );
};
